import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Upload, Trash2, Search, Loader2, Image, Link2, Copy, Check,
  FileImage, FileVideo, File
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAssets = () => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlForm, setUrlForm] = useState({ name: '', url: '', type: 'image' });
  const [copiedId, setCopiedId] = useState(null);

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/content/assets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAssets(await response.json());
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to fetch assets', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAssets(); }, []);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    const token = localStorage.getItem('admin_token');

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_URL}/api/admin/content/assets/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          toast({ title: 'Success', description: `Uploaded ${file.name}` });
        } else {
          const error = await response.json();
          throw new Error(error.detail || 'Upload failed');
        }
      } catch (err) {
        toast({ title: 'Error', description: `Failed to upload ${file.name}: ${err.message}`, variant: 'destructive' });
      }
    }

    setUploading(false);
    fetchAssets();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddUrl = async () => {
    if (!urlForm.name || !urlForm.url) {
      toast({ title: 'Error', description: 'Name and URL are required', variant: 'destructive' });
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/content/assets/url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(urlForm)
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Asset added' });
        setUrlDialogOpen(false);
        setUrlForm({ name: '', url: '', type: 'image' });
        fetchAssets();
      } else {
        throw new Error('Failed to add asset');
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.name}"?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/content/assets/${asset.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Asset deleted' });
      fetchAssets();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const copyUrl = (asset) => {
    const url = asset.url.startsWith('http') ? asset.url : `${API_URL}${asset.url}`;
    navigator.clipboard.writeText(url);
    setCopiedId(asset.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast({ title: 'Copied', description: 'URL copied to clipboard' });
  };

  const filteredAssets = assets.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || a.type === filterType;
    return matchesSearch && matchesType;
  });

  const getIcon = (type) => {
    switch (type) {
      case 'image': return FileImage;
      case 'video': return FileVideo;
      default: return File;
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Assets</h1>
            <p className="text-gray-500 mt-1">Upload and manage media files</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setUrlDialogOpen(true)}>
              <Link2 className="w-4 h-4 mr-2" />
              Add URL
            </Button>
            <Button onClick={() => fileInputRef.current?.click()} className="bg-purple-600 hover:bg-purple-700" disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Tabs value={filterType} onValueChange={setFilterType}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="image">Images</TabsTrigger>
              <TabsTrigger value="video">Videos</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredAssets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Image className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No assets found</p>
              <Button onClick={() => fileInputRef.current?.click()} className="mt-4">
                Upload your first file
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredAssets.map(asset => {
              const Icon = getIcon(asset.type);
              return (
                <Card key={asset.id} className="group hover:shadow-lg transition-shadow overflow-hidden">
                  <div className="aspect-square bg-gray-100 relative">
                    {asset.type === 'image' ? (
                      <img 
                        src={asset.url.startsWith('http') ? asset.url : `${API_URL}${asset.url}`} 
                        alt={asset.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => copyUrl(asset)}>
                        {copiedId === asset.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(asset)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="text-sm font-medium truncate">{asset.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <Badge variant="secondary" className="text-xs">{asset.type}</Badge>
                      <span className="text-xs text-gray-400">{formatSize(asset.file_size)}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add URL Dialog */}
        <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Asset from URL</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={urlForm.name}
                  onChange={(e) => setUrlForm({ ...urlForm, name: e.target.value })}
                  placeholder="Asset name"
                />
              </div>
              <div>
                <Label>URL *</Label>
                <Input
                  value={urlForm.url}
                  onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUrlDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddUrl} className="bg-purple-600 hover:bg-purple-700">Add Asset</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAssets;
